import moment from "moment";
import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import { FaChevronRight } from "react-icons/fa";
import { FaChevronLeft } from "react-icons/fa";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { uuidValidateV4 } from "../helper";
import { TimetableContext } from "../contextApi/TimetableContext";
import { LECTURE_TYPE_ITEMS, getEventColor } from "./constant";
import WarningPublishedDialog from "./WarningPublishedDialog";

import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper";

const CalenderViewTimeTable = ({
  lectures,
  tests = [],
  setIsAddEventModalOpen,
  formData,
  setFormData,
  handleOpenLectureModal,
  handleOpenPTMModal,
  handleOpenCustomHolidayModal,
  handleOpenEditTest,
  setViewTestDetails,
  setViewPastEvents,
}) => {
  const { pastLectures, pastTests } = useContext(TimetableContext);
  const [updatedLectures, setUpdatedLectures] = useState([]);
  const calendarRef = useRef(null);
  const [hasAllDayEvents, setHasAllDayEvents] = useState(true);

  const [isOpenWarningDialog, setOpenWarningDialog] = useState(false);
  const [editingRowData, setEditingRowData] = useState(null);

  moment.locale("es-es", {
    week: {
      dow: new Date().getDay(), // start week from the current day
    },
  });
  const localizer = momentLocalizer(moment);
  const today = new Date();

  const {
    components,
    defaultDate,
    scrollToTime,
    minTime,
    maxTime,
    views,
    formats,
  } = useMemo(
    () => ({
      //   views: { week: true, day: true },
      defaultDate: new Date(),
      scrollToTime: new Date(2011, 1, 1, 8),
      minTime: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        5,
        30
      ),
      maxTime: new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59
      ),
      formats: {
        eventTimeRangeFormat: () => {
          return "";
        },
        timeGutterFormat: (date, culture, localizer) =>
          localizer.format(date, "h A", culture),
      },
      components: {
        toolbar: CustomToolbar,

        week: {
          event: CustomWeekEvent,
          header: CustomHeader,
        },
      },
    }),
    []
  );

  useEffect(() => {
    const allTests = modifyTestSchema();
    const modifyNewEvents = allLectures();
    setUpdatedLectures([...modifyNewEvents, ...allTests]);
  }, [lectures, tests]);

  useEffect(() => {
    // Add or remove class based on the presence of all-day events
    if (calendarRef.current) {
      if (hasAllDayEvents) {
        calendarRef.current.classList.remove("hide-allday-cell");
      } else {
        calendarRef.current.classList.add("hide-allday-cell");
      }
    }
  }, [hasAllDayEvents]);

  const offlineTest = (testData) => {
    let allDay = false;
    const { testStartTime, testEndTime, testDate } = testData;
    const startTime = moment(new Date(testStartTime), "HH:mm");
    const endTime = moment(new Date(testEndTime), "HH:mm");

    const eventStartDate = moment(testDate)
      .set("hour", startTime.hour())
      .set("minute", startTime.minute());

    const eventEndDate = moment(testDate)
      .set("hour", endTime.hour())
      .set("minute", endTime.minute());

    // if test is more than 4h long -> consider day-long event
    if (moment.duration(eventEndDate.diff(eventStartDate)).as("hours") >= 4) {
      setHasAllDayEvents(true);
      allDay = true;
    }

    return {
      ...testData,
      lectureStartTime: new Date(eventStartDate),
      lectureEndTime: new Date(eventEndDate),
      title: testData.testType,
      classType: "Test",
      mode: "Offline",
      allDay,
    };
  };
  const onlineTest = (testData, mode = "only") => {
    const { onlineTestDate, onlineTestEndDate, testDate } = testData;

    const eventStartTime = moment(
      mode === "both" ? onlineTestDate : testDate
    ).startOf("day");
    const eventEndTime = moment(onlineTestEndDate).endOf("day");

    return {
      ...testData,
      lectureStartTime: new Date(eventStartTime),
      lectureEndTime: new Date(eventEndTime),
      title: testData.testType,
      classType: "Test",
      mode: "Online",
      allDay: true,
    };
  };

  const modifyTestSchema = () => {
    const allTests = [];

    [...(tests ?? []), ...(pastTests ?? [])].forEach((testData) => {
      if (testData?.mode.toLowerCase() === "offline") {
        const testCard = offlineTest(testData);
        allTests.push(testCard);
      } else if (testData?.mode.toLowerCase() === "online") {
        const testCard = onlineTest(testData);
        allTests.push(testCard);
      } else {
        const onlineTestCard = onlineTest(testData, "both");
        const onfflineTestCard = offlineTest(testData);
        allTests.push(onfflineTestCard);
        allTests.push(onlineTestCard);
      }
    });
    return allTests;
  };

  const allLectures = () => {
    const alterLectures = lectures.map((obj, index) => ({
      ...obj,
      lectureIndex: index,
    }));

    const allEvents = [...alterLectures, ...(pastLectures ?? [])];

    return allEvents.map((event) => {
      let classType = event.classType || "BREAK";
      let allDay = false,
        eventStartTime,
        eventEndTime,
        title =
          LECTURE_TYPE_ITEMS.includes(classType) && event?.subject
            ? `${event?.subject?.substring(0, 3)} | ${event.lectureName}`
            : classType;

      if (event.classType?.toLowerCase() !== "holiday") {
        if (event?.lectureStartTime && event?.lectureEndTime) {
          eventStartTime = moment(event?.lectureStartTime);
          eventEndTime = moment(event?.lectureEndTime);
        } else if (event?.startTime && event?.endTime) {
          const startTime = event?.startTime.split(":");
          const endTime = event?.endTime.split(":");

          eventStartTime = moment(event?.date)
            .set("hour", startTime[0])
            .set("minute", startTime[1]);
          eventEndTime = moment(event?.date)
            .set("hour", endTime[0])
            .set("minute", endTime[1]);
        }
      } else if (event?.classType?.toLowerCase() === "holiday") {
        const { lectureStartTime, lectureEndTime } = event;

        eventStartTime = moment(lectureStartTime).startOf("day");
        eventEndTime = moment(lectureEndTime).endOf("day");

        title = `${event.lectureName}`;
        allDay = true;
        setHasAllDayEvents(true);
      } else {
        const { date } = event;
        const startTime = moment(event.startTime, "HH:mm");
        const endTime = moment(event.endTime, "HH:mm");

        eventStartTime = moment(date)
          .set("hour", startTime.hour())
          .set("minute", startTime.minute());

        eventEndTime = moment(date)
          .set("hour", endTime.hour())
          .set("minute", endTime.minute());
      }

      // if event is more than 4h long -> consider day-long event
      if (
        event.classType?.toLowerCase() !== "holiday" &&
        moment.duration(eventEndTime.diff(eventStartTime)).as("hours") > 4
      ) {
        setHasAllDayEvents(true);
        allDay = true;
      }

      return {
        ...event,
        lectureStartTime: new Date(eventStartTime),
        lectureEndTime: new Date(eventEndTime),
        title,
        classType,
        allDay,
      };
    });
  };

  const isAllowEditing = (e) => {
    const autoPopulateLectures = [];
    const isCurrentLecturePublished = e?.id && !uuidValidateV4(e?.id);

    lectures.forEach((lect) => {
      if (lect?.isAutoPopulateLecture) autoPopulateLectures.push(lect);
    });

    if (autoPopulateLectures.length && isCurrentLecturePublished) {
      setOpenWarningDialog(true);
      setEditingRowData(e);
    } else handleSelectEvent(e);
  };

  const closeEditing = () => {
    setOpenWarningDialog(false);
    setEditingRowData(null);
    return;
  };

  // edit event
  const handleSelectEvent = (e) => {
    //  check for autoPopulateLectures & OPEN MODAL & return
    if (e.classType?.toLowerCase() === "break") {
      // view only for BREAK
      setViewPastEvents({ isOpen: true, viewLectureData: e });
      return;
    }

    const isSelectedDatePast = moment(e.lectureStartTime).isBefore(
      moment(),
      "minutes"
    );

    if ((isSelectedDatePast || e.isImportedTest) && e.classType === "Test") {
      // view past/ongoing tests
      setViewTestDetails({ isViewTestOpen: true, viewTestData: e });
      return;
    }

    if (isSelectedDatePast) {
      // view previous events s
      setViewPastEvents({ isOpen: true, viewLectureData: e });
      return;
    }

    if (e.classType.toLowerCase() === "test") {
      // edit tests
      handleOpenEditTest(e);
      return;
    }

    // edit existing events scheduled in future
    const selectedDate = moment(e.lectureStartTime)
      .set({ hour: 0, minute: 0, second: 0 })
      .format();
    const selectedStartTime = moment(e.lectureStartTime).format("HH:mm");
    const selectedEndTime = moment(e.lectureEndTime).format("HH:mm");

    const {
      id,
      classType,
      subject,
      roomId,
      roomName,
      lectureId,
      facultyId,
      facultyName,
      bachTimeTablePsidMapping,
      msTeam,
      documentS3Key,
    } = e;

    const indexInLecturesArr = id
      ? lectures.findIndex((item) => item.id === id)
      : e.lectureIndex;

    setFormData({
      ...formData,
      id,
      date: selectedDate,
      startTime: selectedStartTime,
      endTime: selectedEndTime,
      subject,
      room: { label: roomName, value: roomId },
      lecture: lectureId,
      faculty: { label: facultyName, name: facultyName, value: facultyId },
      classType,
      selectedIndex: indexInLecturesArr,
      isMSteams: msTeam?.value,
      msTeam,
      bachTimeTablePsidMapping: bachTimeTablePsidMapping,
      documentS3Key,
    });

    if (["ptm", "orientation"].includes(classType.toLowerCase()))
      handleOpenPTMModal(classType);
    else if (classType.toLowerCase() === "holiday")
      handleOpenCustomHolidayModal(indexInLecturesArr, e);
    else handleOpenLectureModal(classType);

    setEditingRowData(null);
  };

  // create new event fn
  const handleSelectSlot = (e) => {
    // add new events
    const isSelectedDatePast = moment(e.start).isBefore(moment(), "minutes");
    const selectedDate = moment(e.start)
      .set({ hour: 0, minute: 0, second: 0 })
      .format();
    const selectedStartTime = moment(e.start).format("HH:mm");

    const greaterEndTime =
      moment(e.end) >
      moment(e.start).add(formData?.duration.value || 60, "minutes")
        ? moment(e.end)
        : moment(e.start).add(formData?.duration.value || 60, "minutes");

    const selectedEndTime = greaterEndTime.format("HH:mm");

    if (selectedStartTime === selectedEndTime || isSelectedDatePast) return;

    setIsAddEventModalOpen(true);
    setFormData({
      ...formData,
      date: selectedDate,
      startTime: selectedStartTime,
      endTime: selectedEndTime,
      selectedIndex: -1,
      isMSteams: true,
    });
  };

  return (
    <div ref={calendarRef}>
      <Calendar
        localizer={localizer}
        defaultDate={defaultDate}
        defaultView={Views.WEEK}
        //   dayLayoutAlgorithm="no-overlap"
        formats={formats}
        scrollToTime={scrollToTime}
        minTime={minTime} // Start time at 5:30 AM
        maxTime={maxTime} // End time at 12:00 AM
        views={views}
        events={updatedLectures}
        components={components}
        startAccessor="lectureStartTime"
        endAccessor="lectureEndTime"
        style={{
          height: 500,
          backgroundColor: "white",
          marginLeft: "1.3rem",
          marginRight: "1.3rem",
        }}
        onSelectEvent={isAllowEditing}
        onSelectSlot={handleSelectSlot}
        selectable
        eventPropGetter={(updatedLectures) => {
          const isPastEvent = moment(updatedLectures.lectureEndTime).isBefore(
            moment(),
            "minutes"
          );

          let backgroundColor = "#B0B0B0"; // assume grey color
          let color = "#fff",
            border = "1px solid white";
          if (updatedLectures.isMissingLecture) {
            backgroundColor = "#F5F5F5";
            color = "#0F0F0F";
            border = "2px dotted #CBCBCB";
          } else if (!isPastEvent && updatedLectures.classType === "Test")
            backgroundColor = "#45239A";
          else if (!isPastEvent)
            backgroundColor = getEventColor(updatedLectures.classType);

          return {
            style: { backgroundColor, color, border },
          };
        }}
      />

      <WarningPublishedDialog
        isOpen={isOpenWarningDialog}
        setOpenWarningDialog={setOpenWarningDialog}
        rowData={editingRowData}
        onConfirm={handleSelectEvent}
        onCancel={closeEditing}
      />
    </div>
  );
};

export default CalenderViewTimeTable;

const CustomToolbar = ({ date, onNavigate, localizer }) => {
  const startDate = moment(date);
  const endDate = new Date(date);
  endDate.setDate(endDate.getDate() + 6);

  const formatDate = (date) => {
    return localizer.format(date, "MMM DD");
  };

  const formatEndDate = (date) => {
    const endDate = moment(date);
    if (startDate.isSame(endDate, "month")) return localizer.format(date, "DD");
    return localizer.format(date, "MMM DD");
  };

  return (
    <div className="rbc-custom-toolbar d-flex align-items-center">
      <span className="rbc-toolbar-label">{`${formatDate(
        startDate
      )} - ${formatEndDate(endDate)}`}</span>

      <IconButtonWrapper
        sx={{ height: " 30px", width: "30px" }}
        className="mr-3"
        onClick={() => onNavigate("PREV", date)}
      >
        <FaChevronLeft className="cursor" style={{ width: "11px" }} />
      </IconButtonWrapper>

      <IconButtonWrapper
        sx={{ height: "30px", width: "30px" }}
        onClick={() => onNavigate("NEXT", date)}
      >
        <FaChevronRight className="cursor" style={{ width: "11px" }} />
      </IconButtonWrapper>
    </div>
  );
};

const CustomHeader = ({ label, date }) => {
  const labels = label.split(" ");
  const isTodayDate = moment(date).isSame(moment(), "day");

  return (
    <div className="d-flex flex-column align-items-center justify-content-between">
      <div className="text-uppercase color-black" style={{ fontSize: "10px" }}>
        {labels[1]}
      </div>
      <div
        style={{
          borderRadius: "50%",
          backgroundColor: isTodayDate ? "#00B0F5" : "#e2e0e096",
          height: "36px",
          width: "36px",
          textAlign: "center",
        }}
      >
        <p
          className="text-bold mb-0"
          style={{
            fontSize: "20px",
            lineHeight: "1.9",
            color: isTodayDate ? "#fff" : "#000",
          }}
        >
          {labels[0]}
        </p>
      </div>
    </div>
  );
};

const CustomWeekEvent = ({ event, title }) => {
  const formatTime = (time24hr) => {
    const time = moment(time24hr, "HH:mm");
    return time.format("hh:mm a");
  };

  return (
    <>
      <p
        className="text-xs text-bold my-0 text-nowrap"
        style={{ lineHeight: event.allDay ? 2 : "" }}
      >
        {title}
      </p>
      {!event.allDay && (
        <p className="mb-0 text-nowrap" style={{ fontSize: "9px" }}>
          {formatTime(event.startTime || event.lectureStartTime)} -
          {formatTime(event.endTime || event.lectureEndTime)}
        </p>
      )}
    </>
  );
};
